import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Footer } from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import dkLogo from '@/assets/damian-keyes-logo.png';
import heroImage from '@/assets/release-plan-hero.png';
import logosBar from '@/assets/logos-bar.png';

const TYPEFORM_URL = 'https://a23j5fry30t.typeform.com/to/CzjaFT5z';

function getDeviceType() {
  return window.innerWidth < 768 ? 'mobile' : 'desktop';
}

export default function FreePlan() {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(true);
  const [errors, setErrors] = useState<{ firstName?: string; email?: string; agreed?: string }>({});

  // Track page view on load
  useEffect(() => {
    supabase.from('free_plan_page_views').insert({
      device_type: getDeviceType(),
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
    }).then(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: typeof errors = {};
    if (!firstName.trim()) newErrors.firstName = 'Please enter your name';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Please enter a valid email';
    if (!agreed) newErrors.agreed = 'Please agree to the privacy policy';
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    // Track lead
    await supabase.from('free_plan_leads').insert({
      first_name: firstName.trim(),
      email: email.trim(),
      device_type: getDeviceType(),
      user_agent: navigator.userAgent,
      referrer: document.referrer || null,
    });

    // Fire Meta Pixel Lead event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Lead');
    }

    // Fire-and-forget AC sync
    supabase.functions.invoke('sync-activecampaign', {
      body: { email: email.trim(), name: firstName.trim(), tag: 'Free Release Plan 2026' },
    }).catch(() => {});

    const url = `${TYPEFORM_URL}#first_name=${encodeURIComponent(firstName.trim())}&email=${encodeURIComponent(email.trim())}`;
    window.location.href = url;
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col">
      {/* Header */}
      <header className="flex justify-start px-5 lg:px-8 py-5 lg:py-6">
        <img src={dkLogo} alt="DK Music Academy" className="h-11 lg:h-12 w-auto brightness-0" />
      </header>

      <main className="flex-1 flex flex-col items-center px-5 pb-12 lg:pb-16">
        {/* Free Download badge — centered on page */}
        <div className="w-full flex justify-center mb-6">
          <span className="font-bold text-sm tracking-wide uppercase px-5 py-2 rounded-lg border border-gray-300 text-gray-500">
            Free Download 🚀
          </span>
        </div>

        {/* Desktop: split layout */}
        <div className="w-full max-w-6xl lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
          {/* Left: Hero image */}
          <div className="flex justify-center lg:justify-end mb-6 lg:mb-0">
            <img
              src={heroImage}
              alt="20 Day Release Plan preview with artist testimonials"
              className="w-full max-w-xs lg:max-w-md xl:max-w-lg rounded-2xl drop-shadow-2xl"
            />
          </div>

          {/* Right: Copy + Form */}
          <div className="flex flex-col items-center lg:items-start">
            {/* Headline */}
            <h1 className="text-[28px] lg:text-[36px] xl:text-[40px] leading-tight font-extrabold text-center lg:text-left max-w-md mb-4">
              Steal My Exact <span className="inline-block px-3 py-1 rounded-lg" style={{ backgroundColor: '#FFD700' }}>"20 Day Plan"</span> For Your Next Music Release
            </h1>

            <p className="text-sm text-gray-500 font-medium text-center lg:text-left mb-4">⭐ Downloaded by 100,000+ artists</p>

            {/* Subheadline */}
            <p className="text-gray-600 text-center lg:text-left text-[15px] lg:text-base leading-relaxed max-w-sm mb-5">
              Get the step-by-step release plan that's helped 10,000+ independent artists grow their streams, build real fans, and release music with confidence.
            </p>

            {/* Form section */}
            <div className="w-full max-w-sm">
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => { setFirstName(e.target.value); setErrors(prev => ({ ...prev, firstName: undefined })); }}
                    className="w-full h-12 px-4 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 text-base focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500"
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>

                <div>
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: undefined })); }}
                    className="w-full h-12 px-4 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 text-base focus:outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-500"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                {/* Custom green checkbox — auto-checked */}
                <label className="flex items-start gap-2.5 py-1 cursor-pointer select-none">
                  <button
                    type="button"
                    onClick={() => { setAgreed(!agreed); setErrors(prev => ({ ...prev, agreed: undefined })); }}
                    className={cn(
                      'mt-0.5 h-5 w-5 shrink-0 rounded border-2 flex items-center justify-center transition-all duration-200',
                      agreed
                        ? 'border-green-500 bg-green-500 text-white animate-checkbox-pop'
                        : 'border-gray-300 bg-white'
                    )}
                  >
                    {agreed && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  </button>
                  <span className="text-xs text-gray-500 leading-relaxed">
                    I accept the{' '}
                    <a href="/privacy-policy" target="_blank" className="underline hover:text-gray-700">privacy policy</a>.
                  </span>
                </label>
                {errors.agreed && <p className="text-red-500 text-xs">{errors.agreed}</p>}

                <button
                  type="submit"
                  className="group w-full h-14 bg-black text-white font-bold text-lg rounded-lg hover:bg-gray-900 hover:shadow-lg active:scale-[0.98] transition-all duration-150 relative overflow-hidden"
                >
                  <span className="absolute inset-0 animate-btn-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" />
                  <span className="relative flex items-center justify-center gap-1">
                    Get The Plan
                    <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
                  </span>
                </button>
              </form>

            </div>

            {/* Trust signals */}
            <div className="flex items-center justify-center lg:justify-start gap-5 mt-4 text-xs text-gray-500">
              <span>⚡ Takes 3 minutes</span>
              <span>🚀 Free download</span>
              <span>🔒 Secure data</span>
            </div>
          </div>
        </div>

        {/* Logo bar */}
        <div className="mt-14 lg:mt-20 text-center w-full max-w-6xl">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-6 font-medium">We've worked with</p>
          <div className="flex items-center justify-center">
            <img
              src={logosBar}
              alt="Partner logos including Universal, Spotify, Sony, Tidal, Cinq Music"
              className="h-16 lg:h-20 w-auto opacity-50"
            />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
